import { useEffect, useState, useMemo } from 'react';
import { Award, Trophy, ShieldCheck, TrendingUp } from 'lucide-react';
import { certificatesAPI } from '@/lib/api';
import { formatInt } from '@/lib/format';

// Compact type presentation — kept in-file so this component drops in with zero
// external deps.
const TYPE_META = {
  payout:         { label: 'Performance Reward', icon: Trophy,      tint: '#00ff66' },
  funded:         { label: 'Performance Trader', icon: TrendingUp,  tint: '#00ff66' },
  phase_1_passed: { label: 'Phase 1 Passed',     icon: ShieldCheck, tint: '#33ff85' },
  phase_2_passed: { label: 'Phase 2 Passed',     icon: ShieldCheck, tint: '#33ff85' },
};

function initials(name = '') {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]?.toUpperCase() || '').join('') || 'TSG';
}

function CertCard({ c }) {
  const meta = TYPE_META[c.cert_type] || TYPE_META.payout;
  const Icon = meta.icon;
  const date = c.issued_at ? new Date(c.issued_at).toLocaleDateString(undefined, { month: 'short', year: 'numeric' }) : '';
  const hasImage = !!c.has_image && !!c.image_url;
  const imgSrc = hasImage ? `${process.env.REACT_APP_BACKEND_URL}${c.image_url}` : null;
  // Showcase-only certs (no user_id, admin-uploaded) don't need an outbound link.
  const Wrapper = c.showcase_only ? 'div' : 'a';
  const wrapperProps = c.showcase_only ? {} : { href: `/cert/${c.certificate_id}`, target: '_blank', rel: 'noopener noreferrer' };
  return (
    <Wrapper
      {...wrapperProps}
      className={`shrink-0 card-tech transition group ${hasImage
        ? 'w-[320px] rounded-xl overflow-hidden p-0 hover:border-[#00ff66]/50'
        : 'w-[300px] rounded-xl p-5 hover:border-[#00ff66]/50'}`}
      data-testid={`showcase-cert-${c.certificate_id}`}
    >
      {hasImage ? (
        <>
          <div className="relative aspect-[4/3] bg-black">
            <img
              src={imgSrc}
              alt={`${c.trader_name || 'Trader'} certificate`}
              className="w-full h-full object-cover"
              loading="lazy"
              draggable={false}
            />
            <div className="absolute top-3 left-3 flex items-center gap-2 rounded-md bg-black/70 backdrop-blur px-2 py-1 border border-white/10">
              <Icon size={12} style={{ color: meta.tint }} />
              <span className="label-xs">{meta.label}</span>
            </div>
            <div className="absolute top-3 right-3 chip chip-green text-[10px]">VERIFIED</div>
          </div>
          <div className="p-4 border-t border-white/5 flex items-end justify-between gap-3">
            <div className="min-w-0">
              <div className="font-display text-lg truncate">{c.trader_name || 'Trader'}</div>
              <div className="text-[11px] text-zinc-500 truncate">{c.plan_name || (c.account_size ? `$${formatInt(c.account_size)} account` : '—')}</div>
            </div>
            <div className="text-right shrink-0">
              {c.cert_type === 'payout' && c.amount != null ? (
                <>
                  <div className="label-xs">REWARD</div>
                  <div className="font-mono text-lg text-[#00ff66] mt-0.5">${formatInt(c.amount)}</div>
                </>
              ) : (
                <>
                  <div className="label-xs">ISSUED</div>
                  <div className="font-mono text-sm text-zinc-300 mt-0.5">{date}</div>
                </>
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <Icon size={14} style={{ color: meta.tint }} />
              <span className="label-xs">{meta.label}</span>
            </div>
            <div className="chip chip-green text-[10px]">VERIFIED</div>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center font-mono text-sm border border-white/10"
                 style={{ background: 'radial-gradient(closest-side, rgba(0,255,102,0.18), rgba(0,255,102,0.02))' }}>
              {initials(c.trader_name || 'Trader')}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-display text-lg truncate">{c.trader_name || 'Trader'}</div>
              <div className="text-[11px] text-zinc-500 truncate">{c.plan_name || '—'}</div>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-white/5 flex items-end justify-between">
            <div>
              <div className="label-xs">ACCOUNT</div>
              <div className="font-mono text-sm text-white mt-0.5">
                {c.account_size ? `$${formatInt(c.account_size)}` : '—'}
              </div>
            </div>
            <div className="text-right">
              {c.cert_type === 'payout' && c.amount != null ? (
                <>
                  <div className="label-xs">REWARD</div>
                  <div className="font-mono text-lg text-[#00ff66] mt-0.5">${formatInt(c.amount)}</div>
                </>
              ) : (
                <>
                  <div className="label-xs">ISSUED</div>
                  <div className="font-mono text-sm text-zinc-300 mt-0.5">{date}</div>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </Wrapper>
  );
}

/**
 * Infinite marquee of publicly-showcased certificates.
 * We duplicate the list so the CSS animation can loop without seam.
 * Renders nothing if the admin hasn't opted any certificate into the showcase.
 */
export default function CertificateShowcase() {
  const [certs, setCerts] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    certificatesAPI.publicList(24)
      .then(list => setCerts(Array.isArray(list) ? list : []))
      .catch(() => setCerts([]))
      .finally(() => setLoaded(true));
  }, []);

  const loop = useMemo(() => [...certs, ...certs], [certs]);

  if (!loaded || certs.length === 0) return null;

  return (
    <section className="relative overflow-hidden border-y border-white/5 bg-[#050505] py-20" data-testid="certificate-showcase">
      <div className="max-w-7xl mx-auto px-6 mb-10">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="label-sm mb-3">Wall of consistency</div>
            <h2 className="font-display text-4xl sm:text-5xl">Traders we've celebrated.</h2>
            <p className="text-sm text-zinc-500 mt-2 max-w-lg">
              A rolling look at recent Performance Trader and Performance Reward certificates. Every cert is a
              trader who demonstrated the discipline and consistency our programs are built for.
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <Award size={14} className="text-[#00ff66]" />
            <span className="font-mono">{certs.length} certificates featured</span>
          </div>
        </div>
      </div>

      {/* Edge fades */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-24 z-10 bg-gradient-to-r from-[#050505] to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-24 z-10 bg-gradient-to-l from-[#050505] to-transparent" />

      {/* Infinite marquee */}
      <div className="relative">
        <div className="flex gap-4 marquee-track" data-testid="showcase-marquee">
          {loop.map((c, i) => <CertCard key={`${c.certificate_id}-${i}`} c={c} />)}
        </div>
      </div>

      <style>{`
        .marquee-track {
          width: max-content;
          animation: tsg-marquee 40s linear infinite;
        }
        .marquee-track:hover { animation-play-state: paused; }
        @keyframes tsg-marquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .marquee-track { animation: none; }
        }
      `}</style>
    </section>
  );
}
